/**
 * Pure helper for building the Firestore document body of a new
 * `HouseholdChore`. Mirrors iOS `CreateChoreSheet` save path
 * (`Views/Chores/ChoresView.swift:328-637`) and Android
 * `AddChoreSheet.kt:111-390`.
 *
 * Web previously wrote bare `{title, created_at, created_by,
 * household_id}` from `Chores.handleAddChore`, which dropped points,
 * category, recurrence_type, due_date, and assignees. Chores created
 * on web therefore appeared as undifferentiated rows on every other
 * surface and didn't fire reminders. This helper closes that gap.
 */

import { Timestamp } from "firebase/firestore";

export type ChoreCategoryValue =
  | "kitchen"
  | "bathroom"
  | "bedroom"
  | "yard"
  | "pets"
  | "laundry"
  | "general";

export type ChoreRecurrenceValue =
  | "Daily"
  | "Weekdays"
  | "Weekly"
  | "Monthly";

export interface CreateChoreInput {
  title: string;
  description?: string;
  category?: ChoreCategoryValue;
  /** iOS title-case canonical: "Daily" / "Weekdays" / "Weekly" / "Monthly". */
  recurrenceType?: ChoreRecurrenceValue;
  /** Integer ≥ 0. Dropped if 0. */
  points?: number;
  /** ISO date string (YYYY-MM-DD) or undefined. */
  dueDate?: string;
  /** Pre-assigned member uids. iOS sets `assignees` at create. */
  assignees?: string[];
  householdId: string;
  createdBy: string;
}

export interface CreateChoreDoc {
  title: string;
  description: string | null;
  category: ChoreCategoryValue | null;
  recurrence_type: ChoreRecurrenceValue | null;
  points: number | null;
  due_date: Timestamp | null;
  assignees: string[];
  created_at: Timestamp;
  created_by: string;
  household_id: string;
}

export function buildCreateChoreDoc(
  input: CreateChoreInput,
): CreateChoreDoc | null {
  const title = input.title.trim();
  if (!title) return null;
  if (!input.householdId || !input.createdBy) return null;

  const description = input.description?.trim();
  const points = input.points && input.points > 0 ? input.points : null;
  const recurrence = input.recurrenceType ?? null;

  // ISO date → Timestamp at local midnight
  let dueDate: Timestamp | null = null;
  if (input.dueDate) {
    const [yStr, mStr, dStr] = input.dueDate.split("-");
    const y = Number(yStr);
    const m = Number(mStr);
    const d = Number(dStr);
    if (Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)) {
      const localMidnight = new Date(y, m - 1, d, 0, 0, 0, 0);
      dueDate = Timestamp.fromDate(localMidnight);
    }
  }

  return {
    title,
    description: description ? description : null,
    category: input.category ?? null,
    recurrence_type: recurrence,
    points,
    due_date: dueDate,
    assignees: input.assignees ?? [],
    created_at: Timestamp.now(),
    created_by: input.createdBy,
    household_id: input.householdId,
  };
}

export const CHORE_CATEGORIES: ChoreCategoryValue[] = [
  "kitchen",
  "bathroom",
  "bedroom",
  "yard",
  "pets",
  "laundry",
  "general",
];

export const CHORE_RECURRENCE_OPTIONS: ChoreRecurrenceValue[] = [
  "Daily",
  "Weekdays",
  "Weekly",
  "Monthly",
];
